import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { parentPortalAPI } from '../utils/api';
import type { ParentAuthContextType, ParentProfile } from '../types/parent';

const getSubscriptionStatus = (profile: ParentProfile | null) => profile?.subscription_status || 'none';
const isActiveSubscription = (status: string) => status === 'active';

const ParentAuthContext = createContext<ParentAuthContextType | undefined>(undefined);

const PARENT_STORAGE_KEY = 'parent_user';
const PARENT_TOKEN_KEY = 'parent_jwttoken';
const PARENT_REFRESH_KEY = 'parent_refresh_token';

const extractProfile = (response: any): ParentProfile | undefined =>
  response?.data || response?.parent || response?.profile || response?.user;

export const useParentAuth = () => {
  const context = useContext(ParentAuthContext);
  if (!context) {
    throw new Error('useParentAuth must be used within a ParentAuthProvider');
  }
  return context;
};

export const ParentAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [parent, setParent] = useState<ParentProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const storedParent = localStorage.getItem(PARENT_STORAGE_KEY);
    const token = localStorage.getItem(PARENT_TOKEN_KEY);
    if (storedParent && token) {
      try {
        setParent(JSON.parse(storedParent));
      } catch {
        localStorage.removeItem(PARENT_STORAGE_KEY);
        localStorage.removeItem(PARENT_TOKEN_KEY);
        localStorage.removeItem(PARENT_REFRESH_KEY);
      }
    }
    setLoading(false);
  }, []);

  const refreshProfile = useCallback(async () => {
    const response = await parentPortalAPI.getMe();
    const profile = extractProfile(response);
    if (profile) {
      setParent(profile);
      localStorage.setItem(PARENT_STORAGE_KEY, JSON.stringify(profile));
    }
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await parentPortalAPI.login({ username, password });
      if (response.success === false || response.success === 0) {
        throw new Error(response.message || 'Invalid username or password.');
      }
      const profile = response.profile || response.parent || response.user;
      const token = response.access_token || response.jwttoken || profile?.jwttoken;
      if (!profile || !token) {
        throw new Error(response.message || 'Login response did not include a parent profile or token.');
      }
      localStorage.setItem(PARENT_STORAGE_KEY, JSON.stringify(profile));
      localStorage.setItem(PARENT_TOKEN_KEY, token);
      if (response.refresh_token) {
        localStorage.setItem(PARENT_REFRESH_KEY, response.refresh_token);
      }
      try {
        const meResponse = await parentPortalAPI.getMe();
        const refreshedProfile = extractProfile(meResponse);
        if (refreshedProfile) {
          setParent(refreshedProfile);
          localStorage.setItem(PARENT_STORAGE_KEY, JSON.stringify(refreshedProfile));
          return refreshedProfile;
        }
      } catch {
        // Keep the login profile if the follow-up profile refresh is unavailable.
      }
      setParent(profile);
      localStorage.setItem(PARENT_STORAGE_KEY, JSON.stringify(profile));
      return profile;
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Unable to sign in.';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(PARENT_STORAGE_KEY);
    localStorage.removeItem(PARENT_TOKEN_KEY);
    localStorage.removeItem(PARENT_REFRESH_KEY);
    setParent(null);
    setError(null);
  }, []);

  const markPasswordChanged = useCallback(() => {
    setParent((current) => {
      if (!current) return current;
      const updated = { ...current, must_change_password: false };
      localStorage.setItem(PARENT_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const value: ParentAuthContextType = {
    parent,
    subscriptionStatus: getSubscriptionStatus(parent),
    isSubscribed: isActiveSubscription(getSubscriptionStatus(parent)),
    loading,
    error,
    login,
    logout,
    clearError: () => setError(null),
    refreshProfile,
    markPasswordChanged,
  };

  return <ParentAuthContext.Provider value={value}>{children}</ParentAuthContext.Provider>;
};
